import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Button,
  ListBox,
  type ListBoxItemProps,
  SelectValue,
  type ValidationResult
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { Description, FieldError } from "./Field";
import { Label } from "./Label";
import { DropdownItem, DropdownSection, type DropdownSectionProps } from "./ListBox";
import { Popover } from "./Popover";
import { composeTailwindRenderProps, focusRing } from "./utils";

const styles = tv({
  extend: focusRing,
  base: "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition",
  variants: {
    isDisabled: {
      false: "text-gray-800 hover:bg-gray-50 cursor-pointer",
      true: "cursor-not-allowed opacity-50"
    }
  }
});

export interface SelectProps<T extends object> extends Omit<AriaSelectProps<T>, "children"> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children: ReactNode | ((item: T) => ReactNode);
  prefix?: ReactNode;
}

function RASelect<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  prefix,
  ...props
}: SelectProps<T>) {
  return (
    <AriaSelect
      {...props}
      className={composeTailwindRenderProps(props.className, "group relative flex flex-col gap-1")}
    >
      {label && <Label data-testid="select-label">{label}</Label>}
      <Button className={styles}>
        {prefix}
        <SelectValue className="flex-1 truncate text-sm placeholder-shown:text-gray-500" />
        <ChevronDown aria-hidden className="h-4 w-4 opacity-50" />
      </Button>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <Popover className="min-w-(--trigger-width)">
        <ListBox
          {...(items !== undefined && { items })}
          className="outline-hidden max-h-[inherit] overflow-auto p-1 [clip-path:inset(0_0_0_0_round_.75rem)]"
        >
          {children}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

function RASelectItem(props: ListBoxItemProps) {
  return <DropdownItem {...props} />;
}

export function RASelectSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection {...props} />;
}

export function Select<T extends object>(props: SelectProps<T>) {
  return <RASelect {...props} />;
}

export function SelectItem(props: ListBoxItemProps) {
  return (
    <RASelectItem
      {...props}
      className={({ isFocused, isSelected }) =>
        `my-item ${isFocused ? "focused" : ""} ${isSelected ? "selected" : ""}`
      }
    />
  );
}
